import { Component, computed, input } from '@angular/core';
import { DatePipe, DecimalPipe } from '@angular/common';
import { Card } from './card';
import { formatAge } from './format-age';
import { formatBytes } from './format-bytes';
import { InfoTip } from './info-tip';
import { DEAD_LETTER_STATUS_LABELS, ServiceBusEntity } from './models';
import { portalResourceUrl } from './portal-link';
import { deadLetterTone } from './tone';

// One queue or topic with its message counts; dead letters are what the card is about.
@Component({
  selector: 'app-dead-letter-card',
  imports: [Card, DatePipe, DecimalPipe, InfoTip],
  templateUrl: './dead-letter-card.html',
  styleUrl: './dead-letter-card.css',
})
export class DeadLetterCard {
  readonly entity = input.required<ServiceBusEntity>();
  // The page's clock and the age after which a reading counts as old; the page owns both so every card agrees.
  readonly now = input(new Date());
  readonly staleAfterMs = input(Infinity);

  protected readonly deadLetterHelp =
    'Messages that could not be delivered or processed, so Service Bus put them aside. They stay there until someone looks at them, sends them again or deletes them.';
  protected readonly topicHelp =
    'A topic has no dead-letter queue of its own: every subscription has one. The counts here add up all subscriptions of the topic.';

  protected readonly readable = computed(
    () => this.entity().status === 'Clean' || this.entity().status === 'DeadLettered',
  );
  protected readonly tone = computed(() => deadLetterTone(this.entity().status));
  protected readonly badge = computed(() => {
    const entity = this.entity();
    if (entity.status !== 'DeadLettered') {
      return DEAD_LETTER_STATUS_LABELS[entity.status];
    }
    const count = entity.deadLetterMessageCount;
    return `${count.toLocaleString('en')} dead ${count === 1 ? 'letter' : 'letters'}`;
  });

  protected readonly size = computed(() => formatBytes(this.entity().sizeInBytes));
  protected readonly age = computed(() => formatAge(this.entity().checkedAt, this.now()));
  protected readonly stale = computed(
    () => this.now().getTime() - new Date(this.entity().checkedAt).getTime() > this.staleAfterMs(),
  );
  protected readonly lastAccessed = computed(() => {
    const accessed = this.entity().accessedAt;
    return accessed === null ? null : formatAge(accessed, this.now());
  });

  // Anything but Active (for example Disabled or ReceiveDisabled) stops messages being delivered or taken.
  protected readonly notActive = computed(() => {
    const status = this.entity().entityStatus;
    return status !== null && status !== 'Active' ? status : null;
  });

  protected readonly subscriptionsOpen = computed(() => this.entity().status === 'DeadLettered');

  // The resource itself, where the messages can be inspected in Service Bus Explorer.
  protected readonly portalUrl = computed(() => {
    const { subscriptionId, resourceGroup, namespace, kind, name } = this.entity();
    return portalResourceUrl(subscriptionId, resourceGroup, [
      'Microsoft.ServiceBus',
      'namespaces',
      namespace,
      kind === 'Queue' ? 'queues' : 'topics',
      name,
    ]);
  });
}
